import { useChannels } from "api/channels";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { maxVisibleConditionDataCount } from "components/Layout/Segment/SegmentForm/SegmentForm";
import { MoreElements } from "components/MoreElements/MoreElements";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { get } from "lodash";
import { useState } from "react";
import { BoughtInSpecificChannel as BoughtInSpecificChannelType } from "types/segments";
import { BoxElement } from "./BoxElement";
import { useStyles } from "./SegmentConditions.styles";

export const initialValuesBoughtInSpecificChannel: BoughtInSpecificChannelType =
  {
    channelIds: [],
    type: "bought_through_channel",
  };

export function BoughtInSpecificChannel({
  formField,
  value,
  isEdit,
  values,
  status,
  readOnly,
}: {
  formField: string;
  value?: {
    channelIds: string[];
  };
  isEdit?: boolean;
  values: any;
  status: any;
  readOnly?: boolean;
}) {
  const [selectedChannels, setSelectedChannels] = useState<
    { id: string; label: string }[]
  >([]);
  const classes = useStyles();
  const { message } = useMessage();
  const { useInfiniteChannelsList, useChannelsData } = useChannels(
    value?.channelIds,
  );
  const { data: channelData } = useChannelsData();
  const [search, setSearch] = useState("");

  const {
    data: infiniteChannelsList,
    size: channelsSize,
    setSize: setChannelsSize,
    isValidating,
  } = useInfiniteChannelsList(
    search
      ? {
          filters: [
            {
              condition: "like",
              query: "name",
              value: search,
            },
          ],
          order: "asc",
          rows: 50,
          sortKey: "name",
        }
      : {
          order: "asc",
          rows: 50,
          sortKey: "name",
        },
  );

  const { infiniteData: channelsList, anyElementsToFetch: anyChannelsToFetch } =
    useManageInfiniteScroll(infiniteChannelsList || []);

  const initialValue = !isEdit
    ? selectedChannels
    : channelData
      ? Array.isArray(channelData)
        ? channelData.map((channel: { channelId: string; name: string }) => ({
            id: channel.channelId,
            label: channel.name,
          }))
        : {
            id: channelData.channelId,
            label: channelData.name,
          }
      : [];

  const items = channelsList.map((channel) => ({
    label: channel.name || channel.channelId,
    id: channel.channelId,
  }));

  const field = get(values, formField);
  const channelIdsError = get(status, "channelIds");

  const selectedChannelIds = field?.channelIds
    ?.slice(0, maxVisibleConditionDataCount)
    ?.map(
      (channelId: string) =>
        items.find((item) => item.id === channelId)?.label || channelId,
    )
    .join(", ");

  return (
    <>
      {field.isConditionSaved || readOnly ? (
        <>
          {message({ id: "is_one_of" }).toLocaleLowerCase()}{" "}
          <BoxElement content={selectedChannelIds} error={channelIdsError} />
          <MoreElements
            totalItemsNumber={field?.channelIds?.length}
            tooltipData={{
              type: "labels",
              data: field?.channelIds
                ?.slice(maxVisibleConditionDataCount)
                ?.map(
                  (channelId: string) =>
                    items.find((channel) => channel.id === channelId)?.label,
                ),
            }}
          />
        </>
      ) : (
        <SelectInput
          className={classes.input}
          multiple
          displaySearch
          data-test-id="segment-conditions-bought-in-specific-channel-select"
          items={items}
          label={<RequiredFieldLabel text={message({ id: "channels" })} />}
          name={`${formField}.channelIds`}
          asyncSearch={(searchedQuery: string) => setSearch(searchedQuery)}
          placeholder={message({ id: "chooseChannel" })}
          initialValue={initialValue}
          onChange={(_e, newValue) => {
            const selectedIndex = selectedChannels.findIndex(
              (channel) => channel.id === newValue,
            );
            const isSelected = selectedIndex > -1;

            if (isSelected) {
              const values = [...selectedChannels];
              values.splice(selectedIndex, 1);
              return setSelectedChannels(values);
            }

            const itemDetails = items.find(
              (channel) => channel.id === newValue,
            );

            setSelectedChannels([
              ...selectedChannels,
              itemDetails as { id: string; label: string },
            ]);
          }}
          anyElementsToFetch={anyChannelsToFetch}
          loadMoreElements={() =>
            !isValidating && setChannelsSize(channelsSize + 1)
          }
        />
      )}
    </>
  );
}
